import { useMutation, type QueryClient } from "@tanstack/react-query";
import { captureException } from "@sentry/react";
import { isAxiosError } from "axios";
import { apiClient, signApiRequest } from "./";
import type { CustomerCredentials } from "../ui/customerDataStore";

const patchCustomerSalary = async (
  salary: number,
  credentials: CustomerCredentials,
): Promise<void> => {
  const signedRequest = await signApiRequest(
    "PATCH",
    `${import.meta.env.VITE_API_URL}/user/user?fid=${credentials.identityId}`,
    credentials,
    { "content-type": "application/json" },
    { user: { salary } },
  );
  await apiClient(signedRequest);
};

export const usePatchCustomerSalary = (
  credentials: CustomerCredentials,
  queryClient: QueryClient,
) => {
  return useMutation<void, Error, number>({
    mutationFn: (salary) => patchCustomerSalary(salary, credentials),
    retry: false,
    // Awaited so the save stays pending until the recomputed forecast lands.
    // Invalidated, not reset: dropping the cached calculation sends the overview
    // back to its full screen loader.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["get-customer-info-by-id"],
        }),
        queryClient.invalidateQueries({ queryKey: ["get-calculation-by-id"] }),
      ]),
    onError: (error) => {
      // The raw axios error carries the request body, and the salary must not reach
      // Sentry. Report a sanitized error correlated only by fid.
      const status = isAxiosError(error) ? error.response?.status : undefined;
      captureException(
        new Error(
          `patchCustomerSalary failed${status ? ` (status ${status})` : ""}`,
        ),
        {
          tags: {
            operation: "patch-customer-salary",
            errorType: error.name || "UnknownError",
          },
          extra: {
            apiEndpoint: "/user/user?fid=${identityId}",
            fid: credentials.identityId,
            status,
          },
          level: "error",
        },
      );
    },
  });
};
